import { useQueryClient, UseQueryOptions } from "@tanstack/react-query"
import { AxiosError } from "axios"

import { flagsKeys } from "src/data/flags/flagsQueries"
import {
  taskKeys,
  useTaskCache,
} from "src/data/taskManagement/taskManagementQueryCache"
import {
  useMinutDelete,
  useMinutPatch,
  useMinutPost,
  useMinutPut,
  useMinutQuery,
} from "src/hooks/minutQuery"

import { TFetchTasks, TTaskFetchFilters } from "./taskManagementTypes"

export function usePostTask() {
  const taskCache = useTaskCache()

  return useMinutPost<"/organizations/{organization_id}/tasks", "403">({
    pathFn: (path) => `/organizations/${path.organization_id}/tasks`,
    options: {
      onSuccess: () => {
        return taskCache.invalidateTaskList()
      },
    },
  })
}

export function useFetchTasks<Select = TFetchTasks>({
  orgId,
  filters,
  options,
}: {
  orgId: string
  filters?: TTaskFetchFilters
  options?: UseQueryOptions<TFetchTasks, AxiosError, Select, string[]>
}) {
  return useMinutQuery<`/organizations/{organization_id}/tasks`, "403", Select>(
    {
      apiPath: "/organizations/{organization_id}/tasks",
      queryPath: `/organizations/${orgId}/tasks`,
      queryKey: taskKeys.taskList(filters),
      filters,
      options: {
        enabled: options?.enabled,
        select: options?.select,
      },
    }
  )
}

export function usePatchTask() {
  const taskCache = useTaskCache()

  return useMinutPatch<
    "/organizations/{organization_id}/tasks/{task_id}",
    "403"
  >({
    pathFn: (path) =>
      `/organizations/${path.organization_id}/tasks/${path.task_id}`,
    options: {
      onSuccess: () => {
        return taskCache.invalidateTaskList()
      },
    },
  })
}

export function useDeleteTask() {
  const taskCache = useTaskCache()

  return useMinutDelete<"/organizations/{organization_id}/tasks/{task_id}">({
    pathFn: (path) =>
      `/organizations/${path.organization_id}/tasks/${path.task_id}`,
    options: {
      onSuccess: () => {
        return taskCache.invalidateTaskList()
      },
    },
  })
}

export function usePutEnrollBeta() {
  const queryClient = useQueryClient()
  return useMinutPut<"/organizations/{organization_id}/tasks/enroll">({
    pathFn: (path) => `/organizations/${path.organization_id}/tasks/enroll`,
    options: {
      onSuccess: () => {
        return queryClient.invalidateQueries(flagsKeys.all())
      },
    },
  })
}
